import { ComponentProps, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useFormInputState } from '@/hooks/useFormInputState';
import { hasOnlyLatinChars, hasOnlyNumbers, isValidEmail, isValidSiret } from '@/utils/formUtils';

import FormErrorV2 from '@/components/common/form/FormErrorV2';
import Icon from '@/components/common/iconedLinks/Icon';

import styles from './FormFieldV2.module.scss';
import { ERROR_TYPE, INTRACOM_NUMBER_REGEX } from '@/constants/form';
import { URLS } from '@/constants/urls';

export type TextFieldV2Props = {
  id?: string;
  label: string;
  validators?: string[];
  hintMessage?: string;
  country?: string;
  rest?: Record<string, any>;
  onChange?: (event: any) => void;
  onBlur?: () => void;
} & ComponentProps<'input'>

export const TextFieldV2 = (props: TextFieldV2Props): React.JSX.Element => {
  const {
    id, name, label, validators, required, disabled, hintMessage, type = 'text', country, onChange, onBlur, ...rest
  } = props;

  const { register, formState: { errors }, clearErrors } = useFormContext();
  const { isFilled } = useFormInputState({ name });

  const isIntracomNumberValidated = async (intracomNumber) => {
    if (intracomNumber && !intracomNumber.match(INTRACOM_NUMBER_REGEX[country])) {
      return false;
    }
    clearErrors(name);
    return true;
  };

  const validate = Object.fromEntries([
    validators?.includes(ERROR_TYPE.INTRACOM_NUMBER) && [ERROR_TYPE.INTRACOM_NUMBER, isIntracomNumberValidated],
    validators?.includes(ERROR_TYPE.SIRET) && [ERROR_TYPE.SIRET, isValidSiret],
    validators?.includes(ERROR_TYPE.ONLY_NUMBERS) && [ERROR_TYPE.ONLY_NUMBERS, hasOnlyNumbers],
    validators?.includes(ERROR_TYPE.ONLY_LATIN_CHARS) && [ERROR_TYPE.ONLY_LATIN_CHARS, hasOnlyLatinChars],
    validators?.includes(ERROR_TYPE.EMAIL) && [ERROR_TYPE.EMAIL, isValidEmail]
  ].filter(Boolean));

  const [isFocused, setIsFocused] = useState(false);

  const labelClassName = [
    styles.label,
    (isFilled || isFocused) ? styles['label-visible'] : styles['label-invisible']
  ].filter(Boolean).join(' ');

  const inputProps = {
    id,
    rbf: `input${id ? '-' + id : ''}`,
    'data-test-id': `input${id ? '-' + id : ''}`,
    type,
    className: [
      styles.input,
      (isFilled || isFocused) && styles['input-focus'],
      errors?.[name] && styles['input-error']
    ].filter(Boolean).join(' '),
    name,
    placeholder: label,
    disabled,
    ...rest
  };

  const handleOnBlur = () => {
    setIsFocused(false);
    onBlur?.();
  };

  return (
    <div className={styles.container}>
      <label className={labelClassName}>{label}</label>
      {errors?.[name] && (
        <Icon
          src={URLS.DELETE_CIRCLE_ICON_RED}
          alt="arrow"
          width={20}
          height={20}
          className={styles.icon}
        />
      )}
      <input
        {...inputProps}
        {...register(name, {
          required,
          validate,
          onChange
        })}
        onFocus={() => setIsFocused(true)}
        onBlur={handleOnBlur}
      />
      {hintMessage && <div className={styles.hint}>{hintMessage}</div>}
      {errors?.[name] && <FormErrorV2 error={errors[name]} />}
    </div>
  );
};

export default TextFieldV2;
